import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Drawer,
  Empty,
  Image,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CloseOutlined,
  LogoutOutlined,
  PictureOutlined,
  QuestionCircleOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  addDiscussionMessage,
  getOrCreateDM,
  getOrCreateDiscussion,
  getUserDirectory,
  inviteToDiscussion,
  leaveDiscussion,
  markThreadRead,
  subscribeDiscussion,
  subscribeDiscussionMessages,
} from "../helper/discussions";
import { getInitials } from "../helper/userDisplay";
import { uploadFile } from "../helper/firebaseUpload";
import {
  MessageActionsMenu,
  MessageBody,
  useMessageEdit,
} from "./discussionMessage";

const dirName = (u) => (u?.name && u.name !== "N/A" ? u.name : u?.email || "Teammate");

/**
 * Reusable discussion thread Drawer for the admin web. A thread has two axes:
 *   kind "dm"    — pass otherUid (+ otherName); 1:1, no member management, no
 *     question button; title is the other person's name.
 *   kind "group" — either ANCHORED (subjectType "task"|"inspection"|"property",
 *     pass subjectId + title + propertyId + participants seed) or STANDALONE
 *     (subjectType "none", pass threadDocId of an existing group). Group threads
 *     get a members affordance and an "ask a question" (escalation) path;
 *     standalone groups additionally get add-people / leave controls.
 * Photos upload to Storage discussions/.
 */
const DiscussionDrawer = ({
  open,
  onClose,
  kind,
  subjectType,
  subjectId,
  threadDocId = null,
  title,
  propertyName,
  propertyId = null,
  participants = [],
  otherUid,
  otherName,
  authorName,
}) => {
  // A dm is detected by kind (v2: dm = kind "dm", subjectType "none").
  const isDm = kind === "dm";
  const isAnchored = !isDm && ["task", "inspection", "property"].includes(subjectType);
  // Whether people can be added to this thread (the "Add people" button + the
  // question picker). Every non-DM thread supports it: task/property are
  // employee-readable, standalone groups gate on membership, and inspection threads
  // are membership-based too (an invited person is unioned into participants and
  // the rules grant them access). A DM is a fixed pair, so no.
  const canInviteOthers = !isDm;

  const [threadId, setThreadId] = useState(null);
  const [thread, setThread] = useState(null);
  const [notes, setNotes] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [dirList, setDirList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyImages, setReplyImages] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [posting, setPosting] = useState(false);
  const [asking, setAsking] = useState(false);
  const [questionTo, setQuestionTo] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addUids, setAddUids] = useState([]);
  const stagedCountRef = useRef(0);
  const navigate = useNavigate();
  // Shared edit / soft-delete of your own messages (also used by the propertyTasks
  // task-thread drawer), so both renderers stay in lockstep.
  const edit = useMessageEdit(threadId);

  const myUid = auth.currentUser?.uid || null;
  // Title once opened: the subject's own name (task title, "Inspection —
  // {Property}", property name, or a group title), not a generic "Discussion".
  const headerTitle = isDm
    ? `Message — ${otherName || "Teammate"}`
    : title || thread?.title || (isAnchored ? "Discussion" : "Conversation");

  // Create-or-get the thread when the drawer opens, then subscribe.
  useEffect(() => {
    if (!open) {
      setThreadId(null);
      setThread(null);
      setNotes([]);
      setReplyText("");
      setReplyImages([]);
      setQuestionTo([]);
      setAsking(false);
      edit.reset();
      stagedCountRef.current = 0;
      return;
    }
    // A dm needs two distinct people; a missing or self otherUid would collapse
    // the participant pair and the rules would reject the create.
    if (isDm && (!myUid || !otherUid || otherUid === myUid)) {
      setLoading(false);
      message.error("Can't open this conversation");
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    // A standalone group we already know the id of needs no create.
    if (!isDm && !isAnchored && threadDocId) {
      setThreadId(threadDocId);
      return undefined;
    }
    const work = isDm
      ? getOrCreateDM({ otherUid, creatorName: authorName })
      : getOrCreateDiscussion({
          subjectType,
          subjectId,
          title,
          propertyId,
          participants,
          creatorName: authorName,
        });
    work
      .then((d) => {
        if (cancelled) return;
        setThreadId(d.docId);
        setThread(d);
      })
      .catch((e) => {
        console.error("DiscussionDrawer getOrCreate failed", e);
        message.error("Couldn't open discussion");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind, subjectType, subjectId, threadDocId, otherUid]);

  useEffect(() => {
    if (!threadId) return undefined;
    const unsub = subscribeDiscussionMessages(threadId, (list) => {
      setNotes(list);
      setLoading(false);
    });
    return unsub;
  }, [threadId]);

  // Live thread doc → keeps `participants`/`title` current as the Cloud Function
  // reconciles membership (invites, leaves, assignee changes). Without this the
  // member list/count is whatever the create snapshot held — and for a group
  // opened by threadDocId (which skips getOrCreate) `thread` would never load at
  // all, collapsing memberUids to just the current admin.
  useEffect(() => {
    if (!threadId) return undefined;
    return subscribeDiscussion(threadId, (doc) => {
      if (doc) setThread(doc);
    });
  }, [threadId]);

  // Mark read on OPEN and again on CLOSE/unmount (threadId → null) — not per
  // message, which would write to discussion_reads on every snapshot. Stamp the
  // last note's createdAt (kept in a ref) so the marker compares cleanly against
  // lastMessageAt without client-clock skew.
  const lastNoteAtRef = useRef(null);
  useEffect(() => {
    lastNoteAtRef.current = notes.length
      ? notes[notes.length - 1]?.createdAt || null
      : null;
  }, [notes]);
  useEffect(() => {
    if (!threadId || !myUid) return undefined;
    markThreadRead(myUid, threadId, lastNoteAtRef.current);
    return () => markThreadRead(myUid, threadId, lastNoteAtRef.current);
  }, [threadId, myUid]);

  // People directory (uid → {name,image}) for message author avatars + pickers.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    getUserDirectory()
      .then((list) => {
        if (cancelled) return;
        setDirList(list);
        const map = {};
        list.forEach((u) => {
          if (u?.docId) map[u.docId] = u;
        });
        setUsersById(map);
      })
      .catch((e) => console.error("DiscussionDrawer directory load failed", e?.message));
    return () => {
      cancelled = true;
    };
  }, [open]);

  // The membership set: anchored/group threads expose it on the thread doc; the
  // drawer reads the live thread (kept in sync by the create result + invites
  // land via the notify function, surfaced on re-open).
  const memberUids = useMemo(() => {
    const seed = thread?.participants || (isAnchored ? participants : []);
    return [...new Set([myUid, ...(seed || [])].filter(Boolean))];
  }, [thread, participants, isAnchored, myUid]);

  const isStandaloneGroup =
    !isDm && !isAnchored && (subjectType === "none" || !!threadDocId);

  const handleAttach = async (file) => {
    if (stagedCountRef.current >= 6) {
      message.warning("Up to 6 photos per message");
      return false;
    }
    stagedCountRef.current += 1;
    setUploadingImg(true);
    try {
      const url = await uploadFile(file, "discussions");
      if (url) setReplyImages((prev) => [...prev, url]);
      else {
        stagedCountRef.current -= 1;
        message.error("Photo upload failed");
      }
    } catch (e) {
      stagedCountRef.current -= 1;
      message.error("Photo upload failed");
    } finally {
      setUploadingImg(false);
    }
    return false;
  };

  const postMessage = async (type) => {
    if (!threadId) return;
    if (!replyText.trim() && replyImages.length === 0) return;
    setPosting(true);
    try {
      await addDiscussionMessage(threadId, {
        text: replyText,
        type,
        authorName,
        imageUrls: replyImages,
        ...(type === "question" && canInviteOthers
          ? { inviteUids: questionTo, questionTo: questionTo[0] || null }
          : {}),
      });
      setReplyText("");
      setReplyImages([]);
      setQuestionTo([]);
      setAsking(false);
      stagedCountRef.current = 0;
    } catch (e) {
      console.error("post message failed", e);
      message.error(e?.message || "Couldn't post");
    } finally {
      setPosting(false);
    }
  };

  const handleAddPeople = async () => {
    if (!threadId || addUids.length === 0) {
      setAddOpen(false);
      return;
    }
    try {
      await inviteToDiscussion(threadId, { uids: addUids, authorName });
      message.success("Added to the conversation");
    } catch (e) {
      console.error("inviteToDiscussion failed", e);
      message.error(e?.message || "Couldn't add people");
    } finally {
      setAddOpen(false);
      setAddUids([]);
    }
  };

  const handleLeave = async () => {
    if (!threadId) return;
    try {
      await leaveDiscussion(threadId, { authorName });
      message.success("You left the conversation");
      onClose?.();
    } catch (e) {
      console.error("leaveDiscussion failed", e);
      message.error(e?.message || "Couldn't leave");
    }
  };

  const canSend = (!!replyText.trim() || replyImages.length > 0) && !uploadingImg;

  const renderMessage = (n) => {
    // System join/leave markers render as a centered muted status line, not a
    // chat bubble. Compose a readable line from the event + author.
    if (n.type === "system") {
      const added =
        Array.isArray(n.inviteUids) && n.inviteUids.length
          ? n.inviteUids.map((u) => dirName(usersById[u])).join(", ")
          : null;
      const txt =
        n.event === "leave"
          ? `${n.authorName || "Someone"} left`
          : added
            ? `${n.authorName || "Someone"} added ${added}`
            : `${n.authorName || "Someone"} joined`;
      return (
        <div key={n._id} style={{ textAlign: "center", padding: "2px 0" }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {txt}
          </Typography.Text>
        </div>
      );
    }
    return (
      <div
        key={n._id}
        style={{
          borderLeft: `3px solid ${n.type === "question" ? "#fa541c" : "#d9d9d9"}`,
          paddingLeft: 10,
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: "#888",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Avatar size={20} src={usersById[n.authorUid]?.image || undefined}>
            {getInitials(n.authorName)}
          </Avatar>
          <span>
            {n.authorName || "Someone"}
            {n.createdAt ? ` · ${dayjs(n.createdAt).format("MMM D, h:mm A")}` : ""}
            {n.editedAt && !n.deleted ? " · edited" : ""}
          </span>
          {n.type === "question" && !n.deleted && (
            <Tag color="volcano" style={{ marginLeft: 0 }}>
              Question
            </Tag>
          )}
          <MessageActionsMenu
            n={n}
            myUid={myUid}
            onEdit={edit.beginEdit}
            onDelete={edit.confirmDelete}
            style={{ marginLeft: "auto" }}
          />
        </div>
        <MessageBody
          n={n}
          editing={edit.editingId === n._id}
          editText={edit.editText}
          setEditText={edit.setEditText}
          savingEdit={edit.savingEdit}
          onSave={edit.saveEdit}
          onCancel={edit.cancelEdit}
        />
      </div>
    );
  };

  // Directory minus people already in the thread (for the add-people / question
  // pickers).
  const pickableOptions = useMemo(
    () =>
      dirList
        .filter((u) => u?.docId && u.docId !== myUid)
        .map((u) => ({ value: u.docId, label: dirName(u) })),
    [dirList, myUid],
  );
  const addOptions = useMemo(
    () => pickableOptions.filter((o) => !memberUids.includes(o.value)),
    [pickableOptions, memberUids],
  );

  return (
    <Drawer
      title={headerTitle}
      open={open}
      onClose={onClose}
      width={460}
      styles={{ body: { display: "flex", flexDirection: "column" } }}
    >
      {!isDm && (
        <>
          <p
            style={{
              color: "#888",
              marginTop: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>{propertyName || (isAnchored ? "—" : "")}</span>
            {subjectType === "task" && subjectId && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: "auto" }}
                onClick={() => {
                  onClose?.();
                  navigate(`/tasks?task=${subjectId}`);
                }}
              >
                View task ›
              </Button>
            )}
          </p>

          {/* Members affordance: count + avatars, plus group controls. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
              flexWrap: "wrap",
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {memberUids.length} member{memberUids.length === 1 ? "" : "s"}
            </Typography.Text>
            <Avatar.Group maxCount={6} size={24}>
              {memberUids.map((u) => (
                <Tooltip key={u} title={dirName(usersById[u])}>
                  <Avatar size={24} src={usersById[u]?.image || undefined}>
                    {getInitials(dirName(usersById[u]))}
                  </Avatar>
                </Tooltip>
              ))}
            </Avatar.Group>
            {canInviteOthers && (
              <Space size={4} style={{ marginLeft: "auto" }}>
                <Button
                  size="small"
                  icon={<UsergroupAddOutlined />}
                  onClick={() => setAddOpen(true)}
                >
                  Add people
                </Button>
                {/* Leave is group-only: you can't really leave a task/property
                    thread (it's employee-readable), so don't offer it there. */}
                {isStandaloneGroup && (
                  <Popconfirm
                    title="Leave this conversation?"
                    okText="Leave"
                    okButtonProps={{ danger: true }}
                    onConfirm={handleLeave}
                  >
                    <Button size="small" danger icon={<LogoutOutlined />}>
                      Leave
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            )}
          </div>
        </>
      )}

      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : notes.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No messages yet. Start the conversation."
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {notes.map((n) => renderMessage(n))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
        {asking && canInviteOthers && (
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%", marginBottom: 8 }}
            placeholder="Pull people in (optional)"
            optionFilterProp="label"
            value={questionTo}
            onChange={setQuestionTo}
            options={pickableOptions}
          />
        )}
        <Input.TextArea
          rows={3}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder={
            asking ? "What's your question?" : isDm ? "Write a message…" : "Write a reply…"
          }
        />
        {replyImages.length > 0 && (
          <Space wrap size={6} style={{ marginTop: 8 }}>
            {replyImages.map((url, i) => (
              <div key={i} style={{ position: "relative" }}>
                <Image
                  src={url}
                  width={64}
                  height={64}
                  preview={false}
                  style={{ objectFit: "cover", borderRadius: 4 }}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setReplyImages((prev) => prev.filter((_, j) => j !== i));
                    stagedCountRef.current = Math.max(0, stagedCountRef.current - 1);
                  }}
                  style={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    background: "#fff",
                    borderRadius: "50%",
                    boxShadow: "0 0 2px rgba(0,0,0,.3)",
                  }}
                />
              </div>
            ))}
          </Space>
        )}
        <Space style={{ marginTop: 8 }} wrap>
          <Button
            type="primary"
            loading={posting && !asking}
            disabled={!canSend || (asking && posting)}
            onClick={() => postMessage(asking ? "question" : "note")}
          >
            {asking ? "Ask" : "Send"}
          </Button>
          {!isDm &&
            (asking ? (
              <Button
                onClick={() => {
                  setAsking(false);
                  setQuestionTo([]);
                }}
              >
                Cancel
              </Button>
            ) : (
              <Button icon={<QuestionCircleOutlined />} onClick={() => setAsking(true)}>
                Ask a question
              </Button>
            ))}
          <Upload accept="image/*" multiple showUploadList={false} beforeUpload={handleAttach}>
            <Button icon={<PictureOutlined />} loading={uploadingImg}>
              Add photo
            </Button>
          </Upload>
        </Space>
      </div>

      <Modal
        title="Add people"
        open={addOpen}
        onOk={handleAddPeople}
        onCancel={() => {
          setAddOpen(false);
          setAddUids([]);
        }}
        okText="Add"
        okButtonProps={{ disabled: addUids.length === 0 }}
      >
        <Select
          mode="multiple"
          allowClear
          style={{ width: "100%" }}
          placeholder="Pick people to add"
          optionFilterProp="label"
          value={addUids}
          onChange={setAddUids}
          options={addOptions}
        />
      </Modal>
    </Drawer>
  );
};

export default DiscussionDrawer;
